import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MessageCircle, Copy, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { siteApi, apiError, DAYS } from "../api";

const Ctx = createContext({ openBooking: () => {}, enabled: false });
export const useBooking = () => useContext(Ctx);

const todayIso = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };

function hoursWarning(hours, dateStr, time) {
  if (!hours?.published || !dateStr || !time) return null;
  const idx = (new Date(dateStr + "T00:00:00").getDay() + 6) % 7;
  const day = (hours.days || []).find((d) => d.day === idx);
  if (!day) return null;
  if (day.closed) return `Am ${DAYS[idx]} ist der Salon laut veröffentlichten Öffnungszeiten geschlossen.`;
  if (time < day.open || time >= day.close) return `Die gewählte Uhrzeit liegt außerhalb der Öffnungszeiten am ${DAYS[idx]} (${day.open}–${day.close}).`;
  const b = (day.breaks || []).find((x) => time >= x.start && time < x.end);
  if (b) return `Die gewählte Uhrzeit fällt in eine Pause (${b.start}–${b.end}).`;
  return null;
}

function F({ label, htmlFor, error, children, hint }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-[11px] uppercase tracking-wide text-stone-500">{label}</Label>
      <div className="mt-1">{children}</div>
      {error ? <p className="text-xs text-red-700 mt-1" data-testid={`${htmlFor}-error`}>{error}</p> : hint ? <p className="text-[11px] text-stone-400 mt-1">{hint}</p> : null}
    </div>
  );
}

const EMPTY = { name: "", phone: "", address: "", service_id: "", date: "", time: "", message: "", privacy_accepted: false };

function BookingForm({ config, preselect, onDone }) {
  const [f, setF] = useState({ ...EMPTY, service_id: preselect || "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    setF((x) => ({ ...x, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };
  const min = todayIso();
  const warning = useMemo(() => hoursWarning(config.hours, f.date, f.time), [config.hours, f.date, f.time]);

  const validate = () => {
    const er = {};
    if (f.name.trim().length < 2) er.name = "Bitte Ihren vollständigen Namen angeben.";
    if (!/^\+?[\d\s()/-]{6,}$/.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 6) er.phone = "Bitte eine gültige Telefonnummer mit Ländervorwahl angeben (z. B. +49 …).";
    if (f.address.trim().length < 5) er.address = "Bitte Ihre Adresse angeben.";
    if (!f.service_id) er.service_id = "Bitte eine Dienstleistung wählen.";
    if (!f.date) er.date = "Bitte ein Wunschdatum wählen."; else if (f.date < min) er.date = "Das Datum darf nicht in der Vergangenheit liegen.";
    if (!f.time) er.time = "Bitte eine Wunschuhrzeit wählen.";
    if (!f.privacy_accepted) er.privacy_accepted = "Bitte bestätigen Sie den Datenschutzhinweis.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await siteApi.post("/website/appointments", { ...f, name: f.name.trim(), phone: f.phone.trim(), address: f.address.trim(), message: f.message.trim() });
      onDone(r.data);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const grouped = config.categories.map((c) => ({ ...c, items: config.services.filter((s) => s.category_id === c.id) })).filter((c) => c.items.length);
  const rest = config.services.filter((s) => !config.categories.some((c) => c.id === s.category_id));

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="booking-form">
      {config.instructions && <p className="text-sm text-stone-600 bg-[#F1EAE0] rounded-xl px-4 py-3" data-testid="booking-instructions">{config.instructions}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Name *" htmlFor="booking-name" error={errors.name}><Input id="booking-name" data-testid="booking-name" value={f.name} onChange={set("name")} autoComplete="name" /></F>
        <F label="Telefonnummer *" htmlFor="booking-phone" error={errors.phone} hint="Mit Ländervorwahl, z. B. +49 170 1234567"><Input id="booking-phone" data-testid="booking-phone" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" placeholder="+49 …" /></F>
      </div>
      <F label="Adresse *" htmlFor="booking-address" error={errors.address}><Input id="booking-address" data-testid="booking-address" value={f.address} onChange={set("address")} autoComplete="street-address" placeholder="Straße, PLZ Ort" /></F>
      <F label="Dienstleistung *" htmlFor="booking-service" error={errors.service_id}>
        <select id="booking-service" data-testid="booking-service" value={f.service_id} onChange={set("service_id")} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm">
          <option value="">– bitte wählen –</option>
          {grouped.map((g) => <optgroup key={g.id} label={g.name}>{g.items.map((s) => <option key={s.id} value={s.id}>{s.name}{s.duration_minutes ? ` (ca. ${s.duration_minutes} Min.)` : ""}</option>)}</optgroup>)}
          {rest.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </F>
      <div className="grid grid-cols-2 gap-4">
        <F label="Wunschdatum *" htmlFor="booking-date" error={errors.date}><Input id="booking-date" data-testid="booking-date" type="date" min={min} value={f.date} onChange={set("date")} /></F>
        <F label="Wunschuhrzeit *" htmlFor="booking-time" error={errors.time}><Input id="booking-time" data-testid="booking-time" type="time" value={f.time} onChange={set("time")} /></F>
      </div>
      {warning && <p className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="booking-hours-warning"><AlertTriangle className="h-4 w-4 shrink-0" />{warning} Sie können die Anfrage trotzdem senden – der Salon prüft, ob ein Termin möglich ist.</p>}
      <F label="Nachricht (optional)" htmlFor="booking-message"><Textarea id="booking-message" data-testid="booking-message" rows={3} value={f.message} onChange={set("message")} placeholder="Besondere Wünsche, Hinweise …" /></F>
      <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
        <input type="checkbox" data-testid="booking-privacy" checked={f.privacy_accepted} onChange={set("privacy_accepted")} className="mt-0.5 accent-[var(--brand-primary)]" />
        <span>Ich habe die <Link to="/rechtliches/datenschutz" target="_blank" className="underline" data-testid="booking-privacy-link">Datenschutzerklärung</Link> gelesen. Meine Angaben werden ausschließlich zur Bearbeitung der Terminanfrage verwendet und per WhatsApp an den Salon übermittelt. *</span>
      </label>
      {errors.privacy_accepted && <p className="text-xs text-red-700 -mt-2" data-testid="booking-privacy-error">{errors.privacy_accepted}</p>}
      <Button type="submit" disabled={busy} data-testid="booking-submit" className="w-full rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageCircle className="h-4 w-4 mr-2" />}Anfrage vorbereiten
      </Button>
      <p className="text-[11px] text-stone-400 text-center">Im nächsten Schritt öffnet sich WhatsApp mit Ihrer Nachricht – Sie senden sie selbst ab. Ihr Termin gilt erst nach Bestätigung durch den Salon.</p>
    </form>
  );
}

function BookingSuccess({ result, onClose }) {
  const copy = async () => { try { await navigator.clipboard.writeText(result.message_text); toast.success("Nachricht kopiert"); } catch { toast.error("Kopieren nicht möglich"); } };
  return (
    <div className="space-y-5" data-testid="booking-success">
      <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold">Anfrage vorbereitet (Nr. {result.reference})</p>
          <p className="mt-1">Ihr Termin ist <strong>noch nicht bestätigt</strong>. Bitte senden Sie die Anfrage jetzt per WhatsApp an den Salon – wir melden uns zur Bestätigung.</p>
        </div>
      </div>
      <a href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid="booking-whatsapp-link" className="flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold py-3 text-sm transition-colors">
        <MessageCircle className="h-5 w-5" /> WhatsApp öffnen &amp; Nachricht senden
      </a>
      <p className="text-xs text-stone-600 text-center" data-testid="booking-manual-hint">WhatsApp öffnet sich mit einem vorbereiteten Entwurf. <strong>Bitte drücken Sie dort auf „Senden“</strong> – erst dann erhält der Salon Ihre Anfrage.</p>
      <details className="rounded-xl border border-stone-200 p-3">
        <summary className="text-xs font-semibold text-stone-600 cursor-pointer">Nachricht anzeigen / kopieren</summary>
        <pre className="text-xs text-stone-700 whitespace-pre-wrap mt-2 font-sans" data-testid="booking-message-preview">{result.message_text}</pre>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-primary)] mt-2" data-testid="booking-copy"><Copy className="h-3.5 w-3.5" /> Kopieren</button>
      </details>
      <Button variant="outline" onClick={onClose} className="w-full rounded-full" data-testid="booking-close">Schließen</Button>
    </div>
  );
}

export function BookingProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [preselect, setPreselect] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => { siteApi.get("/website/appointments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false, services: [], categories: [] })); }, []);
  const openBooking = (serviceId) => { setPreselect(serviceId || ""); setResult(null); setOpen(true); };
  const enabled = !!config?.enabled && (config.services?.length || 0) > 0;
  return (
    <Ctx.Provider value={{ openBooking, enabled }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-stone-900 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[var(--brand-accent)]" /> Termin anfragen</DialogTitle>
            <DialogDescription>Unverbindliche Terminanfrage – der Salon bestätigt Ihren Termin persönlich.</DialogDescription>
          </DialogHeader>
          {config && (result ? <BookingSuccess result={result} onClose={() => setOpen(false)} /> : <BookingForm config={config} preselect={preselect} onDone={setResult} />)}
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export function BookingButton({ serviceId, className = "", variant = "primary", label = "Termin anfragen", testid = "booking-button" }) {
  const { openBooking, enabled } = useBooking();
  if (!enabled) return null;
  const styles = {
    primary: "bg-[var(--brand-accent)] text-[var(--brand-primary-dark)] hover:bg-[var(--brand-accent-hover)]",
    dark: "bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]",
    outline: "border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white",
    light: "border border-stone-50/40 text-stone-50 hover:bg-white/10",
  };
  return (
    <button type="button" onClick={() => openBooking(serviceId)} data-testid={testid} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-colors ${styles[variant]} ${className}`}>
      <CalendarDays className="h-4 w-4" /> {label}
    </button>
  );
}
